/**
 * Beer Pong — **rules + move log** (WS-17f). Pure, side-effect-free and, exactly like
 * `pool.ts`, deliberately **physics-free**: this file never simulates a throw. The simulator
 * lives in the web app (`apps/web/src/modules/games/beerpong/physics.ts`) because only a
 * client ever needs to run one; the server (push) and the replay engine only need to know
 * *what happened*, which is a fact recorded in the move log.
 *
 * ## The deterministic-throw architecture (same shape Pool established)
 *
 * A turn is exactly **one** entry in the append-only `Y.Array<BeerPongMove>` at
 * `GAME_MOVES_KEY`: the thrower, an aim vector, a `power`, and an `outcome` snapshot. Two
 * things are true at once, on purpose:
 *
 *  1. **The inputs alone reproduce the game.** The rack is a canonical constant
 *     (`beerPongRack()`), the simulator uses a fixed timestep, and the throw is integrated
 *     with nothing but IEEE-754 `+ - * /` and `Math.sqrt` — every one of which is *exactly*
 *     specified by the language, on every engine. Re-running the same `{aim, power}` sequence
 *     from the same rack reproduces the same game.
 *  2. **We still do not bet the game on that.** The thrower's client writes the resulting
 *     `outcome` — which cup went down, which rims it rattled off, where the ball came to rest
 *     — into the same move entry. Every other client re-simulates for the animation, but when
 *     its own result disagrees with the recorded snapshot it **trusts the snapshot** and logs
 *     a warning (`replayThrow` in `apps/web/src/modules/games/beerpong/playback.ts`). A family
 *     game where two phones show a different number of cups is broken in a way that "but our
 *     physics was right" doesn't fix.
 *
 * Consequence: **`deriveBeerPong` reads the snapshot, never the physics.** Given the move log
 * it is a pure, boring state machine that runs identically in a browser, in Node (the push
 * notifier) and in a test — no canvas, no `requestAnimationFrame`, no engine. A move with no
 * `outcome` is simply rejected, the same "a stray move never derails the game" rule Connect-4
 * established, because an un-simulated throw has nothing to apply.
 *
 * ## Why a purpose-built integrator rather than the vendored Planck
 *
 * Pool reaches for Planck because a pool shot genuinely *is* a 2D problem: balls slide on a
 * plane and hit each other. A Beer Pong throw is not — it is a ballistic arc that spends its
 * whole life *above* the cups and interacts with exactly one thing, once, on the way down.
 * Modelling it in a 2D planar world would mean faking the height axis anyway (the only axis
 * that matters), and buying a general contact solver to move a single body.
 *
 * So the simulator is ~120 lines of explicit 3-DOF integration, and the determinism story is
 * *stronger* than Pool's rather than weaker: Pool converts an aim angle to a velocity through
 * `Math.sin`/`Math.cos`, which the spec leaves implementation-approximated. Here the aim is
 * recorded as a **raw vector** (`aimX`/`aimY`, see `BeerPongMove`) and normalised with
 * `Math.sqrt`, so no implementation-defined function ever touches the trajectory. The
 * snapshot guard stays anyway — a determinism claim should be measured, not asserted.
 *
 * ## Rules v1 (what's in, what's deliberately out)
 *
 * Implemented: two players, ten cups each in a standard 4-3-2-1 triangle, strict alternation
 * (one throw per turn each), a made cup is removed from the *defender's* rack, first to clear
 * the opposite rack wins. That's the whole rulebook, on purpose.
 *
 * **Not** implemented, every one a knowing simplification: no re-racks; no "make it, take
 * it"; no redemption/rebuttal throw (so the player who throws first does get one more throw
 * over a game — the honest cost of strict alternation, and the alternative is a draw rule
 * that costs more to explain than it's worth in a family game); no two-balls-per-turn; no
 * bounce-shot-counts-double; no blowing/swatting; no island calls; no drinking, which this
 * app would not model even if it modelled everything else.
 */
/** Ten cups, in a 4-3-2-1 triangle with the apex pointing at the thrower. */
export const BEERPONG_RACK_SIZE = 10;
/**
 * Table geometry, in table units (a real 8ft table is 4 units long here, so one unit is
 * roughly 60cm). Rendering scales these to whatever the canvas is; nothing here is pixels.
 * The width is a shade generous versus a real 2ft table — a 4:1 sliver reads as a corridor
 * on a phone, and the extra width only ever gives a bad throw somewhere to land.
 */
export const BEERPONG_TABLE_LENGTH = 4;
export const BEERPONG_TABLE_WIDTH = 1.2;
/** Rim radius of a cup, and the ball — a ball drops when its centre lands inside
 * `CUP_RADIUS - BALL_RADIUS` of a cup's centre, so the target is 0.043 units wide. */
export const BEERPONG_CUP_RADIUS = 0.075;
export const BEERPONG_BALL_RADIUS = 0.032;
/** How tall a cup stands. The height the ball has to be *below* to be in, and the height a
 * descending ball is tested at. */
export const BEERPONG_CUP_HEIGHT = 0.2;
export const BEERPONG_CENTER_Y = BEERPONG_TABLE_WIDTH / 2;
/** Where the ball leaves the thrower's hand, in the thrower's own frame: just inside their
 * end rail, on the centre line. Fixed — the throw is aim and power, never a stance. */
export const BEERPONG_THROW_X = 0.22;
/** Horizontal distance the ball covers before it falls back to rim height, at `power` 0 and
 * 1. The band is wide enough that the whole rack sits comfortably inside it and short throws
 * still land on the table rather than in a neighbouring county. */
export const BEERPONG_MIN_RANGE = 1;
export const BEERPONG_MAX_RANGE = 4.4;
/** Downward acceleration, table units per second squared. */
export const BEERPONG_GRAVITY = 9.8;
/** Vertical launch speed as a multiple of horizontal — a fixed 45° arc. A constant, not a
 * control: giving the player a loft slider would be a third axis to fight on a phone. */
export const BEERPONG_ARC_RATIO = 1;
/**
 * Storage/comparison precision for snapshot positions — a micrometre-equivalent on a 4-unit
 * table, well below anything visible, and enough to absorb last-bit float noise inside one
 * engine. Same job as `pool.ts`'s `quantize`, but *dividing* by the scale rather than
 * multiplying by its reciprocal: `1e6` is exactly representable and `400000 / 1e6` is
 * correctly rounded to the double nearest `0.4`, whereas `400000 * 1e-6` is
 * `0.39999999999999997`. That difference is the whole reason this helper exists, so it may as
 * well be the accurate version.
 */
const QUANTUM_SCALE = 1e6;
export function quantizeBeerPong(value) {
    return Math.round(value * QUANTUM_SCALE) / QUANTUM_SCALE;
}
/**
 * The canonical rack — the constant that makes "same inputs ⇒ same game" possible at all,
 * expressed in the **thrower's frame**: the thrower stands at low `x` and the rack is at the
 * far end, apex nearest them.
 *
 * ```
 *        0          <- apex, closest to the thrower
 *      1   2
 *    3   4   5
 *  6   7   8   9    <- back row, against the far rail
 * ```
 *
 * Rows are spaced by an exact `sqrt(3)/2` of the cup spacing (`Math.sqrt` is IEEE-exact, so
 * this is bit-identical everywhere), with a hair of gap so cups never overlap.
 *
 * `BACK_ROW_INSET` is the one number here that isn't cosmetic. In a real game you stand
 * *behind* the table and throw over your own cups; on a phone the throwing spot has to be on
 * the table, so instead the racks are set in from each end far enough that the arc is
 * already above cup height by the time it reaches the near edge of the thrower's own rack.
 * The slowest legal throw clears `CUP_HEIGHT` at about x = 0.42, hence 0.55 — otherwise the
 * ball would visibly fly *through* the near player's own back row on every single throw.
 * `apps/web/test/games/beerpong.determinism.test.ts` holds that clearance to the number.
 */
const BACK_ROW_INSET = 0.55;
const RACK_ROWS = [[0], [1, 2], [3, 4, 5], [6, 7, 8, 9]];
/** Both racks in one place: index `i` of the returned array is cup `i`'s centre. */
export function beerPongRack() {
    const spacing = BEERPONG_CUP_RADIUS * 2 * 1.02;
    const rowGap = spacing * (Math.sqrt(3) / 2);
    const backX = BEERPONG_TABLE_LENGTH - BACK_ROW_INSET;
    const cups = new Array(BEERPONG_RACK_SIZE);
    RACK_ROWS.forEach((row, rowIndex) => {
        const x = quantizeBeerPong(backX - (RACK_ROWS.length - 1 - rowIndex) * rowGap);
        row.forEach((cup, i) => {
            cups[cup] = {
                x,
                y: quantizeBeerPong(BEERPONG_CENTER_Y + (i - (row.length - 1) / 2) * spacing),
            };
        });
    });
    return cups;
}
/**
 * The 180° rotation that turns one player's frame into the other's. Both players throw "up
 * the table" in their own frame, which is what lets a single simulator, a single rack
 * constant and a single set of rules serve both sides with no mirrored special cases: the
 * only thing that knows which end of the table you're standing at is the renderer.
 *
 * An involution — `mirror(mirror(p))` is exactly `p` — so there is no "which way round" to
 * get wrong. Exactly, because the result is quantised: `4 - (4 - 1.3)` is `1.2999999999999998`
 * in IEEE doubles, and a mirrored cup centre that drifts by an ulp per round trip is a cup
 * that two clients could disagree about.
 */
export function mirrorPoint(p) {
    return {
        x: quantizeBeerPong(BEERPONG_TABLE_LENGTH - p.x),
        y: quantizeBeerPong(BEERPONG_TABLE_WIDTH - p.y),
    };
}
/**
 * Cup `cup` of the rack defended by player `side`, in **world** coordinates (the fixed frame
 * the table is drawn in: player 0 at low `x`, player 1 at high `x`). Player 1's rack sits at
 * the far end of player 0's frame, so it *is* the canonical rack; player 0's is its mirror.
 */
export function beerPongCupWorld(side, cup, rack = beerPongRack()) {
    const canonical = rack[cup] ?? { x: 0, y: BEERPONG_CENTER_Y };
    return side === 1 ? canonical : mirrorPoint(canonical);
}
/**
 * The launch speed that lands a ball `range` units away, measured at the moment it falls back
 * through rim height — i.e. exactly the point the capture test happens, so the aiming
 * reticle and the simulator agree by construction rather than by tuning.
 *
 * From `z(t) = z0 + a·s·t − g·t²/2` and `range = s·t`, eliminating `t`:
 * `s = range · sqrt(g / (2·(range·a − d)))` where `d = rimHeight − z0`. Pure arithmetic plus
 * one `Math.sqrt`, both exactly specified — see the file docblock on why that matters.
 */
export function beerPongThrowSpeed(range) {
    const drop = BEERPONG_CUP_HEIGHT - BEERPONG_BALL_RADIUS;
    const denominator = 2 * (range * BEERPONG_ARC_RATIO - drop);
    if (denominator <= 0)
        return 0;
    return range * Math.sqrt(BEERPONG_GRAVITY / denominator);
}
/** `power` (0–1) → how far the throw carries. Linear, so the aim reticle moves at a steady
 * rate under the finger rather than accelerating away at the top of the drag. */
export function beerPongRange(power) {
    const clamped = Math.min(1, Math.max(0, power));
    return BEERPONG_MIN_RANGE + clamped * (BEERPONG_MAX_RANGE - BEERPONG_MIN_RANGE);
}
/** Unit vector of an aim, or straight up the table for a degenerate one. `Math.sqrt` rather
 * than `Math.hypot`: the spec pins down the former exactly and leaves the latter to the
 * implementation, and this value multiplies the whole trajectory. */
export function normalizeAim(aimX, aimY) {
    const lengthSq = aimX * aimX + aimY * aimY;
    if (!Number.isFinite(lengthSq) || lengthSq <= 0)
        return { x: 1, y: 0 };
    const length = Math.sqrt(lengthSq);
    return { x: aimX / length, y: aimY / length };
}
/**
 * Where an unobstructed throw would come back down through rim height — the aiming reticle,
 * in the thrower's frame. Not a simulation and deliberately not a trajectory predictor: it
 * knows nothing about cups, rims or bounces, so what it promises is "this is where the ball
 * arrives", never "this goes in".
 */
export function beerPongLanding(aimX, aimY, power) {
    const unit = normalizeAim(aimX, aimY);
    const range = beerPongRange(power);
    return { x: BEERPONG_THROW_X + unit.x * range, y: BEERPONG_CENTER_Y + unit.y * range };
}
function startingState() {
    const full = () => Array.from({ length: BEERPONG_RACK_SIZE }, (_, i) => i);
    return {
        standing: [full(), full()],
        turn: 0,
        throws: [0, 0],
        winner: null,
        lastThrow: null,
        appliedMoves: 0,
        rejectedMoveIndexes: [],
    };
}
/**
 * Applies one throw's `outcome` to `state`, returning the next state. The whole rulebook
 * lives here and nowhere else, and at this tier it genuinely is four lines: take the cup out
 * of the defender's rack, hand the turn over, and if the rack is empty the thrower has won.
 * Pure: `state` is never mutated.
 */
export function resolveThrow(state, thrower, outcome) {
    const defender = thrower === 0 ? 1 : 0;
    const made = outcome.cup;
    const defenderCups = made === null ? state.standing[defender] : state.standing[defender].filter((c) => c !== made);
    const standing = defender === 0 ? [defenderCups, state.standing[1]] : [state.standing[0], defenderCups];
    const cleared = defenderCups.length === 0;
    const throws = [...state.throws];
    throws[thrower] += 1;
    return {
        ...state,
        standing,
        throws,
        // Strict alternation: one throw each, always. See the file docblock for the "make it,
        // take it" and redemption rules deliberately left out.
        turn: cleared ? null : defender,
        winner: cleared ? thrower : null,
        lastThrow: { thrower, cup: made, rimmed: [...outcome.rimmed], cleared },
        appliedMoves: state.appliedMoves + 1,
    };
}
function isApplicable(state, turn, move, playerIds) {
    if (!move || move.throwerId !== playerIds[turn])
        return false;
    if (!Number.isFinite(move.aimX) || !Number.isFinite(move.aimY))
        return false;
    if (!Number.isFinite(move.power))
        return false;
    const outcome = move.outcome;
    if (!outcome || !Array.isArray(outcome.rimmed))
        return false;
    if (outcome.cup === null)
        return true;
    // A snapshot claiming a cup that isn't there is not a snapshot we can trust any of: reject
    // the whole move rather than quietly reinterpret it as a miss.
    if (!Number.isInteger(outcome.cup))
        return false;
    const defender = turn === 0 ? 1 : 0;
    return state.standing[defender].includes(outcome.cup);
}
/**
 * Replays a Beer Pong move log into a state — the `derive` this game kind plugs into the
 * generic replay engine (`replay.ts`), and the same "the move log IS the game" contract
 * Connect-4 has. Every move is re-validated at derive time; an invalid one is skipped, never
 * fatal, so a CRDT merge race or a client that wrote garbage costs at most that one entry.
 */
export function deriveBeerPong(moves, playerIds) {
    let state = startingState();
    const rejectedMoveIndexes = [];
    for (let i = 0; i < moves.length; i += 1) {
        const turn = state.turn;
        const move = moves[i];
        if (turn === null || !isApplicable(state, turn, move, playerIds)) {
            rejectedMoveIndexes.push(i);
            continue;
        }
        state = resolveThrow(state, turn, move.outcome);
    }
    return { ...state, rejectedMoveIndexes };
}
/** Structural equality of two snapshots, to `tolerance` on the resting position. The
 * divergence check that decides whether a re-simulating client trusts itself or the thrower. */
export function throwOutcomesMatch(a, b, tolerance = 1e-4) {
    if (a.cup !== b.cup)
        return false;
    if (a.rimmed.length !== b.rimmed.length)
        return false;
    if (a.rimmed.some((cup, i) => cup !== b.rimmed[i]))
        return false;
    return Math.abs(a.rest.x - b.rest.x) <= tolerance && Math.abs(a.rest.y - b.rest.y) <= tolerance;
}
/** Plain-language line for the UI, e.g. "Rattled out". Kept here so the board, the replay
 * scrubber and any future spectator view all say the same thing. Facts only, never a taunt
 * and never a tally (`memory/no-demand-mechanics.md`). */
export function describeThrow(result) {
    if (!result)
        return '';
    if (result.cleared)
        return 'Last cup — that’s the game';
    if (result.cup !== null)
        return result.rimmed.length > 0 ? 'Rattled in' : 'Sank it';
    if (result.rimmed.length > 0)
        return 'Rattled out';
    return 'Missed';
}
//# sourceMappingURL=beerpong.js.map